/**
 * 第三方面单扫描归一化：从扫码原文中提取用于查单 / 展示的运单号。
 *
 * 规则与后端 `ThirdPartyLabelScanParser.normalizeParcelScan` 逐条对齐（后端是落库口径的唯一来源，
 * 前端这份只用于扫描页即时展示与本地去重，两边改动必须同步）：
 * - SWX / BAG / BTK 内部单号：原样返回
 * - 剥掉括号、GS1 FNC1 分隔符（PDA 扫码枪输出真实 0x1D，手机相机识别库常输出字面 "{GS}"）
 * - USPS：420 + 5 或 9 位邮编 + 可选符号 + 22 位 → 取最后 22 位
 * - FedEx：96 开头共 34 位 → 取最后 14 位并去前导零
 * - 未命中：返回 trim 后的原文
 *
 * desktop-web 的 WMS 换单页另有一份 `trackingNumberParser.ts`（不含 GS / 括号处理），两个 package 无共享模块，
 * 故此处独立一份，与 `BarCodeUtil.ts` 两端各一份的现状一致。
 */

// 内部单号前缀与 BarCodeUtil.ts 保持一致；不直接 import BarCodeUtil 是因为它间接依赖 antd-mobile，
// 纯字符串工具不应带 UI 依赖（也让单测在 jsdom 下不触发 antd-mobile 的 window 告警）
const TRACKING_NUMBER_PREFIX = 'SWX';
const BAG_NUMBER_PREFIX = 'BAG';
const BAG_NUMBER_LENGTH = 16;
const BAG_TASK_NUMBER_PREFIX = 'BTK';

// GS1 FNC1 分隔符：真实控制符 0x1D，以及部分识别库输出的字面写法。
// 控制符用 fromCharCode 构造而不写转义字面量，避免源文件里出现不可见字符
const GS_CHAR = String.fromCharCode(0x1d);
const GS_LITERAL = '{GS}';

// Kotlin 的 trim() 会去掉首尾的 0x1C~0x1F 控制符（FS/GS/RS/US），JS 的 trim() 不会；这里补齐以保证两端 trim 结果一致
const EDGE_CONTROL_RANGE = String.fromCharCode(0x1c) + '-' + String.fromCharCode(0x1f);
const EDGE_TRIM = new RegExp('^[\\s' + EDGE_CONTROL_RANGE + ']+|[\\s' + EDGE_CONTROL_RANGE + ']+$', 'g');

// USPS: 420 + 5或9位ZIP + 可选非数字符号 + 22位系统单号 → 系统单号为最后22位
const USPS_IDENTIFY = /^420(\d{5}|\d{9})\D*\d{22}$/;
const USPS_EXTRACT = /\d{22}$/;

// FedEx: 96 + 32位(总长34) → 系统单号为最后14位且去前导零
const FEDEX_IDENTIFY = /^96[0-9]{32}$/;
const FEDEX_EXTRACT = /\d{14}$/;

export const normalizeParcelScan = (rawScan: string): string => {
    const raw = rawScan.replace(EDGE_TRIM, '');
    if (!raw) {
        return rawScan;
    }

    if (raw.startsWith(TRACKING_NUMBER_PREFIX)) {
        return raw;
    }
    if (raw.startsWith(BAG_NUMBER_PREFIX) && raw.length === BAG_NUMBER_LENGTH) {
        return raw;
    }
    if (raw.startsWith(BAG_TASK_NUMBER_PREFIX)) {
        return raw;
    }

    // 不同设备对 FNC1 的输出不一致，与括号一样统一剥掉后再做规则识别
    const stripped = raw
        .split('(').join('')
        .split(')').join('')
        .split(GS_LITERAL).join('')
        .split(GS_CHAR).join('');

    if (USPS_IDENTIFY.test(stripped)) {
        const match = stripped.match(USPS_EXTRACT);
        return match ? match[0] : raw;
    }
    if (FEDEX_IDENTIFY.test(stripped)) {
        const match = stripped.match(FEDEX_EXTRACT);
        if (!match) {
            return raw;
        }
        const extracted = match[0].replace(/^0+/, '');
        return extracted || '0';
    }
    return raw;
};
